/**
 * Το πρόχειρο της σελίδας «Κατάλογος»: ΜΙΑ αποθήκευση για όλη τη σελίδα (απόφαση ιδιοκτήτη
 * 12/10/2026). Ό,τι αλλάζει (φόρμες προϊόντων/κατηγοριών, σειρά, μαζικές, νέες ομάδες) μένει εδώ
 * ώσπου να πατηθεί «Αποθήκευση»· οι διακόπτες «Διαθέσιμο»/«Κρυφό» της λίστας ΔΕΝ περνούν από εδώ.
 * Η σύγκριση γίνεται πάντα με τη «βάση» (ό,τι έστειλε ο server), όχι με ιστορικό ενεργειών.
 */
import {
  ApiCategory, ApiProduct, ApiStoreIngredient, CatRef, CategoryForm, FormContext, GroupRef, IngRef,
  ProductForm, ServerCategory, TreeShape, buildCategories, catKeyOfRef, cloneTree, formFromProduct,
  moveInTree, parentOf, productKey, treeKeys,
} from './catalogModel';

export interface ProductState {
  form: ProductForm;
  is_available: boolean;
  is_hidden: boolean;
  is_kiosk_recommended: boolean;
  /** συνδεδεμένες ομάδες επιλογών στη σειρά του ταμείου· null = δεν φορτώθηκαν/δεν άλλαξαν */
  groups: GroupRef[] | null;
}

export interface NewOptionGroup {
  ref: string;
  name: string;
  selection_type: 'single' | 'multiple';
  is_required: boolean;
  choices: { name: string; price_modifier: number; is_default: boolean }[];
}

export interface NewSizeGroup {
  ref: string;
  name: string;
  items: { name: string; price_modifier: number; is_default: boolean }[];
}

export interface NewIngredient {
  ref: string;
  name: string;
  price: number;
  category: string;
}

export interface CatalogDraft {
  /** επεξεργασμένα (p:ID) και νέα (np:N) προϊόντα */
  products: Record<string, ProductState>;
  newProducts: string[];
  /** επεξεργασμένες (c:ID) και νέες (nc:N) κατηγορίες */
  categories: Record<string, CategoryForm>;
  newCategories: string[];
  /** ομάδες που μπαίνουν αυτόματα στα νέα προϊόντα της κατηγορίας */
  categoryGroups: Record<string, GroupRef[]>;
  /** σειρά/γονείς κατηγοριών· null = όπως στον server */
  tree: TreeShape | null;
  /** σειρά προϊόντων ανά κατηγορία (κλειδί κόμβου) */
  productOrder: Record<string, string[]>;
  newGroups: NewOptionGroup[];
  newSizeGroups: NewSizeGroup[];
  newIngredients: NewIngredient[];
}

export const emptyDraft = (): CatalogDraft => ({
  products: {}, newProducts: [], categories: {}, newCategories: [], categoryGroups: {}, tree: null,
  productOrder: {}, newGroups: [], newSizeGroups: [], newIngredients: [],
});

let tempSeq = 0;
/** Προσωρινό κλειδί για ό,τι φτιάχνεται στο πρόχειρο (np: προϊόν, nc: κατηγορία, ng: ομάδα, ni: υλικό, nsg: ομάδα μεγεθών). */
export const tempKey = (prefix: 'np' | 'nc' | 'ng' | 'ni' | 'nsg') => `${prefix}:${++tempSeq}`;

/* ───────────────────────── Βάση (ό,τι έχει ο server) ───────────────────────── */

export interface CatalogBase {
  products: ApiProduct[];
  byKey: Record<string, ApiProduct>;
  forms: Record<string, ProductForm>;
  categories: Record<string, ServerCategory>;
  tree: TreeShape;
  systemIds: Set<number>;
  /** κόμβος → προϊόντα στη σειρά του server ('none' = χωρίς κατηγορία) */
  order: Record<string, string[]>;
  /** p:ID → ομάδες επιλογών (μόνο όσα φορτώθηκαν) */
  links: Record<string, GroupRef[]>;
  /** c:ID → προεπιλεγμένες ομάδες (μόνο όσα φορτώθηκαν) */
  catGroups: Record<string, GroupRef[]>;
  library: Map<number, ApiStoreIngredient>;
}

export function buildBase(
  products: ApiProduct[],
  categoryList: ApiCategory[],
  ingredients: ApiStoreIngredient[],
  links: Record<string, GroupRef[]> = {},
  catGroups: Record<string, GroupRef[]> = {},
): CatalogBase {
  const { categories, tree, systemIds } = buildCategories(categoryList);
  const library = new Map(ingredients.map(i => [i.id, i] as [number, ApiStoreIngredient]));
  const ctx: FormContext = { systemIds, library };
  const byKey: Record<string, ApiProduct> = {};
  const forms: Record<string, ProductForm> = {};
  const order: Record<string, string[]> = {};
  products.forEach(p => {
    const key = productKey(p.id);
    byKey[key] = p;
    forms[key] = formFromProduct(p, ctx);
    const node = nodeOfRef(forms[key].category_id, categories);
    (order[node] = order[node] || []).push(key);
  });
  return { products, byKey, forms, categories, tree, systemIds, order, links, catGroups, library };
}

/** Ο κόμβος του δέντρου για μια κατηγορία της φόρμας· άγνωστη (ανενεργή) κατηγορία → «Χωρίς κατηγορία». */
export function nodeOfRef(ref: CatRef, categories: Record<string, unknown>, draft?: CatalogDraft): string {
  const key = catKeyOfRef(ref);
  if (key === 'none') return key;
  if (categories[key] || (draft && draft.categories[key] && draft.newCategories.includes(key))) return key;
  return 'none';
}

export function baseProduct(base: CatalogBase, key: string): ProductState | null {
  const p = base.byKey[key];
  if (!p) return null;
  return {
    form: base.forms[key],
    is_available: p.is_available !== false,
    is_hidden: p.is_hidden === true,
    is_kiosk_recommended: p.is_kiosk_recommended === true,
    groups: base.links[key] ?? null,
  };
}

export const isNewKey = (key: string) => key.startsWith('np:') || key.startsWith('nc:');

/** Το προϊόν όπως φαίνεται τώρα (πρόχειρο ή server). */
export function currentProduct(draft: CatalogDraft, base: CatalogBase, key: string): ProductState | null {
  const d = draft.products[key];
  // Πρόχειρο από πριν φορτωθούν οι ομάδες του: οι ομάδες είναι ακόμη του server.
  if (d) return d.groups === null && base.links[key] ? { ...d, groups: base.links[key].slice() } : d;
  return baseProduct(base, key);
}

export function currentCategoryForm(draft: CatalogDraft, base: CatalogBase, key: string): CategoryForm | null {
  return draft.categories[key] || base.categories[key]?.form || null;
}

export const currentTree = (draft: CatalogDraft, base: CatalogBase): TreeShape => draft.tree || base.tree;

const cloneForm = (f: ProductForm): ProductForm => ({
  ...f,
  sizes: f.sizes.map(s => ({ ...s })),
  simple_ingredients: f.simple_ingredients.map(i => ({ ...i })),
  ingredients: f.ingredients.map(i => ({ ...i })),
});

export const cloneProductState = (s: ProductState): ProductState => ({
  ...s, form: cloneForm(s.form), groups: s.groups ? s.groups.slice() : null,
});

/* ───────────────────────── Αλλαγές προϊόντων ───────────────────────── */

/** Αλλάζει ένα προϊόν του προχείρου (το πρώτο άγγιγμα αντιγράφει τη βάση). */
export function updateProduct(draft: CatalogDraft, base: CatalogBase, key: string, fn: (s: ProductState) => ProductState): CatalogDraft {
  const current = currentProduct(draft, base, key);
  if (!current) return draft;
  const next = fn(draft.products[key] ? current : cloneProductState(current));
  return { ...draft, products: { ...draft.products, [key]: next } };
}

export const updateProductForm = (draft: CatalogDraft, base: CatalogBase, key: string, fn: (f: ProductForm) => ProductForm) =>
  updateProduct(draft, base, key, s => ({ ...s, form: fn(s.form) }));

export function addNewProduct(draft: CatalogDraft, key: string, form: ProductForm, groups: GroupRef[]): CatalogDraft {
  const state: ProductState = { form, is_available: true, is_hidden: false, is_kiosk_recommended: false, groups };
  return { ...draft, products: { ...draft.products, [key]: state }, newProducts: [...draft.newProducts, key] };
}

export function removeNewProduct(draft: CatalogDraft, key: string): CatalogDraft {
  const products = { ...draft.products };
  delete products[key];
  const productOrder: Record<string, string[]> = {};
  Object.keys(draft.productOrder).forEach(node => { productOrder[node] = draft.productOrder[node].filter(k => k !== key); });
  return { ...draft, products, newProducts: draft.newProducts.filter(k => k !== key), productOrder };
}

/** Μετακίνηση προϊόντων σε κατηγορία (μαζική / σύρσιμο στο δέντρο). */
export function moveProducts(draft: CatalogDraft, base: CatalogBase, keys: string[], ref: CatRef): CatalogDraft {
  return keys.reduce((d, key) => updateProductForm(d, base, key, f => ({ ...f, category_id: ref })), draft);
}

/* ───────────────────────── Κατηγορίες ───────────────────────── */

export function updateCategory(draft: CatalogDraft, base: CatalogBase, key: string, fn: (f: CategoryForm) => CategoryForm): CatalogDraft {
  const current = currentCategoryForm(draft, base, key);
  if (!current) return draft;
  return { ...draft, categories: { ...draft.categories, [key]: fn({ ...current }) } };
}

export function addNewCategory(draft: CatalogDraft, base: CatalogBase, key: string, form: CategoryForm, parentKey: string | null): CatalogDraft {
  const tree = cloneTree(currentTree(draft, base));
  if (parentKey) (tree.children[parentKey] = tree.children[parentKey] || []).push(key);
  else tree.roots.push(key);
  return { ...draft, tree, categories: { ...draft.categories, [key]: form }, newCategories: [...draft.newCategories, key] };
}

/** Σβήνει νέα κατηγορία· τα προϊόντα της γυρίζουν στην αρχική τους (τα νέα → «Χωρίς κατηγορία»). */
export function removeNewCategory(draft: CatalogDraft, base: CatalogBase, key: string): CatalogDraft {
  let next: CatalogDraft = { ...draft };
  Object.keys(draft.products).forEach(pk => {
    const s = draft.products[pk];
    if (s.form.category_id !== key) return;
    const orig = base.forms[pk];
    next = updateProductForm(next, base, pk, f => ({ ...f, category_id: orig ? orig.category_id : 0 }));
  });
  const children = (currentTree(draft, base).children[key] || []).slice();
  let tree = cloneTree(currentTree(next, base));
  // Υποκατηγορίες της (μόνο νέες μπορούν να είναι) ανεβαίνουν στη ρίζα.
  children.forEach(child => { tree = moveInTree(tree, child, null, tree.roots.length); });
  tree.roots = tree.roots.filter(k => k !== key);
  Object.keys(tree.children).forEach(p => { tree.children[p] = tree.children[p].filter(k => k !== key); });
  delete tree.children[key];
  const categories = { ...next.categories };
  delete categories[key];
  const categoryGroups = { ...next.categoryGroups };
  delete categoryGroups[key];
  const productOrder = { ...next.productOrder };
  delete productOrder[key];
  return { ...next, categories, categoryGroups, productOrder, tree, newCategories: next.newCategories.filter(k => k !== key) };
}

export function moveCategory(draft: CatalogDraft, base: CatalogBase, key: string, parentKey: string | null, index: number): CatalogDraft {
  const tree = currentTree(draft, base);
  const next = moveInTree(tree, key, parentKey, index);
  return next === tree ? draft : { ...draft, tree: next };
}

/* ───────────────────────── Σειρά προϊόντων ───────────────────────── */

/** Τα προϊόντα ενός κόμβου όπως φαίνονται τώρα: η σειρά του προχείρου, μετά όσα ήρθαν/νέα. */
export function orderedProducts(draft: CatalogDraft, base: CatalogBase, node: string, members: string[]): string[] {
  const memberSet = new Set(members);
  const order = draft.productOrder[node] || base.order[node] || [];
  const out = order.filter(k => memberSet.has(k));
  const placed = new Set(out);
  members.forEach(k => { if (!placed.has(k)) { out.push(k); placed.add(k); } });
  return out;
}

export function setProductOrder(draft: CatalogDraft, node: string, keys: string[]): CatalogDraft {
  return { ...draft, productOrder: { ...draft.productOrder, [node]: keys.slice() } };
}

/* ───────────────────────── Νέες ομάδες / υλικά ───────────────────────── */

export const addNewGroup = (draft: CatalogDraft, ref: string, group: Omit<NewOptionGroup, 'ref'>): CatalogDraft =>
  ({ ...draft, newGroups: [...draft.newGroups, { ...group, ref }] });

export const addNewSizeGroup = (draft: CatalogDraft, ref: string, group: Omit<NewSizeGroup, 'ref'>): CatalogDraft =>
  ({ ...draft, newSizeGroups: [...draft.newSizeGroups, { ...group, ref }] });

export const addNewIngredient = (draft: CatalogDraft, ref: string, ingredient: Omit<NewIngredient, 'ref'>): CatalogDraft =>
  ({ ...draft, newIngredients: [...draft.newIngredients, { ...ingredient, ref }] });

/** Βγάζει μια νέα ομάδα από το πρόχειρο και από κάθε προϊόν/κατηγορία που την είχε. */
export function removeNewGroup(draft: CatalogDraft, ref: string): CatalogDraft {
  const products: Record<string, ProductState> = {};
  Object.keys(draft.products).forEach(k => {
    const s = draft.products[k];
    products[k] = s.groups && s.groups.includes(ref) ? { ...s, groups: s.groups.filter(g => g !== ref) } : s;
  });
  const categoryGroups: Record<string, GroupRef[]> = {};
  Object.keys(draft.categoryGroups).forEach(k => { categoryGroups[k] = draft.categoryGroups[k].filter(g => g !== ref); });
  return { ...draft, products, categoryGroups, newGroups: draft.newGroups.filter(g => g.ref !== ref) };
}

export function removeNewIngredient(draft: CatalogDraft, ref: string): CatalogDraft {
  const products: Record<string, ProductState> = {};
  Object.keys(draft.products).forEach(k => {
    const s = draft.products[k];
    products[k] = s.form.simple_ingredients.some(i => i.store_ingredient_id === ref)
      ? { ...s, form: { ...s.form, simple_ingredients: s.form.simple_ingredients.filter(i => i.store_ingredient_id !== ref) } }
      : s;
  });
  return { ...draft, products, newIngredients: draft.newIngredients.filter(i => i.ref !== ref) };
}

/* ───────────────────────── Μετά από (μερική) αποθήκευση ───────────────────────── */

/** Ό,τι γράφτηκε ήδη στον server: αντιστοιχίες προσωρινών κλειδιών και ολοκληρωμένα κομμάτια. */
export interface SavedSoFar {
  categories: Record<string, number>;   // nc:N → id
  groups: Record<string, number>;       // ng:N → id
  ingredients: Record<string, number>;  // ni:N → id
  sizeGroups: string[];                 // nsg:N
  products: Record<string, number>;     // np:N → id
  updatedProducts: string[];            // p:ID που γράφτηκαν
  updatedCategories: string[];          // c:ID που γράφτηκαν
  tree: boolean;
  orders: string[];                     // κόμβοι με γραμμένη σειρά
}

export const emptySaved = (): SavedSoFar => ({
  categories: {}, groups: {}, ingredients: {}, sizeGroups: [], products: {}, updatedProducts: [], updatedCategories: [], tree: false, orders: [],
});

const remapRef = <T extends string | number>(ref: T, map: Record<string, number>): T | number =>
  (typeof ref === 'string' && map[ref] !== undefined ? map[ref] : ref);

/**
 * Το πρόχειρο που μένει μετά από αποθήκευση που σταμάτησε στη μέση: ό,τι γράφτηκε φεύγει, και οι
 * αναφορές σε όσα δημιουργήθηκαν (nc:/ng:/ni:/np:) γίνονται πραγματικά ids — αλλιώς μια δεύτερη
 * «Αποθήκευση» θα τα ξανάφτιαχνε.
 */
export function remainingDraft(draft: CatalogDraft, saved: SavedSoFar): CatalogDraft {
  const catKey = (k: string) => (saved.categories[k] !== undefined ? `c:${saved.categories[k]}` : k);
  const prodKey = (k: string) => (saved.products[k] !== undefined ? productKey(saved.products[k]) : k);

  const products: Record<string, ProductState> = {};
  Object.keys(draft.products).forEach(k => {
    if (saved.products[k] !== undefined || saved.updatedProducts.includes(k)) return;
    const s = draft.products[k];
    products[k] = {
      ...s,
      groups: s.groups ? s.groups.map(g => remapRef(g, saved.groups)) : null,
      form: {
        ...s.form,
        category_id: remapRef(s.form.category_id, saved.categories),
        simple_ingredients: s.form.simple_ingredients.map(i => ({ ...i, store_ingredient_id: remapRef(i.store_ingredient_id as IngRef, saved.ingredients) })),
      },
    };
  });

  const categories: Record<string, CategoryForm> = {};
  Object.keys(draft.categories).forEach(k => {
    if (saved.categories[k] !== undefined || saved.updatedCategories.includes(k)) return;
    categories[k] = draft.categories[k];
  });
  const categoryGroups: Record<string, GroupRef[]> = {};
  Object.keys(draft.categoryGroups).forEach(k => {
    if (saved.categories[k] !== undefined || saved.updatedCategories.includes(k)) return;
    categoryGroups[k] = draft.categoryGroups[k].map(g => remapRef(g, saved.groups));
  });

  let tree: TreeShape | null = null;
  if (draft.tree && !saved.tree) {
    tree = { roots: draft.tree.roots.map(catKey), children: {} };
    Object.keys(draft.tree.children).forEach(p => { tree!.children[catKey(p)] = draft.tree!.children[p].map(catKey); });
  }

  const productOrder: Record<string, string[]> = {};
  Object.keys(draft.productOrder).forEach(node => {
    if (saved.orders.includes(node)) return;
    productOrder[catKey(node)] = draft.productOrder[node].map(prodKey);
  });

  return {
    ...draft,
    products,
    newProducts: draft.newProducts.filter(k => saved.products[k] === undefined),
    categories,
    newCategories: draft.newCategories.filter(k => saved.categories[k] === undefined),
    categoryGroups,
    tree,
    productOrder,
    newGroups: draft.newGroups.filter(g => saved.groups[g.ref] === undefined),
    newSizeGroups: draft.newSizeGroups.filter(g => !saved.sizeGroups.includes(g.ref)),
    newIngredients: draft.newIngredients.filter(i => saved.ingredients[i.ref] === undefined),
  };
}

/* ───────────────────────── Δέντρο: τι άλλαξε ───────────────────────── */

/** Ο γονέας κάθε κατηγορίας στο δέντρο. */
export function parentsOf(tree: TreeShape): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  tree.roots.forEach(k => { out[k] = null; });
  Object.keys(tree.children).forEach(p => tree.children[p].forEach(k => { out[k] = p; }));
  return out;
}

/** Άλλαξε η σειρά των ΥΠΑΡΧΟΥΣΩΝ κατηγοριών μέσα στον ίδιο γονέα (οι μετακινήσεις γονέα μετρούν χωριστά); */
export function treeOrderChanged(base: TreeShape, draft: TreeShape): boolean {
  const existing = new Set(treeKeys(base));
  const baseParents = parentsOf(base);
  const draftParents = parentsOf(draft);
  const lists = (t: TreeShape) => [t.roots, ...Object.keys(t.children).map(p => t.children[p])];
  // Σε κάθε λίστα: η σειρά όσων ήταν ήδη εκεί (ίδιος γονέας πριν και τώρα).
  const signature = (t: TreeShape, parents: Record<string, string | null>, other: Record<string, string | null>) =>
    lists(t).map(list => list.filter(k => existing.has(k) && parents[k] === other[k]).join(',')).filter(Boolean).sort().join('|');
  return signature(base, baseParents, draftParents) !== signature(draft, draftParents, baseParents);
}

/**
 * Χρειάζεται PUT .../categories/reorder; Όταν άλλαξε σειρά/γονέας υπαρχουσών, ή όταν μια νέα κατηγορία
 * μπήκε ΑΝΑΜΕΣΑ σε υπάρχουσες (στο τέλος αρκεί το display_order του POST).
 */
export function treeNeedsReorder(base: TreeShape, draft: TreeShape): boolean {
  const existing = new Set(treeKeys(base));
  const baseParents = parentsOf(base);
  const draftParents = parentsOf(draft);
  if (Array.from(existing).some(k => baseParents[k] !== draftParents[k])) return true;
  if (treeOrderChanged(base, draft)) return true;
  const lists = [draft.roots, ...Object.keys(draft.children).map(p => draft.children[p])];
  return lists.some(list => {
    const firstNew = list.findIndex(k => !existing.has(k));
    return firstNew >= 0 && list.slice(firstNew).some(k => existing.has(k));
  });
}

/** Ο γονέας μιας κατηγορίας στο τρέχον δέντρο. */
export const currentParent = (draft: CatalogDraft, base: CatalogBase, key: string) => parentOf(currentTree(draft, base), key);
